import type { Nullable, Nullish } from '@payaca/utilities/types';
import type {
  IndustryTypes,
  NumberOfEmployees,
} from './companyInformationTypes';
import type { Features } from './featureTypes';
import type { Integrations } from './integrationTypes';
import type { Pipeline } from './pipelineTypes';
import type { BusinessHours } from './scheduleTypes';
import type { ScheduledEventColourCoding } from './scheduledEventsTypes';

export enum AccountRegions {
  AUSTRALIA = 'au',
  CANADA = 'ca',
  IRELAND = 'ie',
  NEW_ZEALAND = 'nz',
  SOUTH_AFRICA = 'za',
  UK = 'uk',
  US = 'us',
}

export interface Account {
  accountName: string;
  activeSubscription: boolean;
  address?: string;
  city?: string;
  companyName: string;
  connectedToStripe: boolean;
  contactNumber?: string;
  createdAt: string;
  email?: string;
  id: number;
  logoUrl?: string;
  // @deprecated
  paymentTerms?: string;
  proposalPaymentTerms?: Nullish<string>;
  invoicePaymentTerms?: Nullish<string>;
  postcode?: Nullish<string>;
  stripeCustomerId?: Nullish<string>;
  stripePaymentMethodBrand?: string;
  stripeCurrentPeriodEndsAt?: string;
  stripePaymentMethodExpMonth?: string;
  stripePaymentMethodExpYear?: string;
  stripePaymentMethodLast4?: string;
  stripeConnectUserId?: Nullable<string>;
  updatedAt: string;
  uuid: string;
  vatNumber?: string;
  accountNumberRaw?: string;
  accountNameRaw?: string;
  sortCodeRaw?: string;
  businessNotes?: string;
  brandColour?: string;
  chatAgentInstructions?: Nullish<string>;
  integrations: Integrations;
  isCisSubcontractor: boolean;
  cisDeductionRate: null | number;
  legalBusinessName: string;
  region: AccountRegions;
  propositionValidForDays?: number;
  invoiceDueInDays?: number;
  gasSafeRegNo?: string;
  niceicRegNo?: string;
  mcsCertificationNumber?: string | null;
  mcsBatteryStorageCertified?: boolean;
  solarRetailTariffMinorPerKwh?: number | null;
  solarExportTariffMinorPerKwh?: number | null;
  solarPlacementRules?: Record<string, unknown> | null;
  solarEessSettings?: Record<string, unknown> | null;
  allowStripePaymentDefault?: boolean;
  allowBankTransferPaymentDefault?: boolean;
  smsSenderHandle?: string;
  industryType?: IndustryTypes | string;
  numberOfEmployees?: NumberOfEmployees | string;
  companyRegistrationNumber?: string;
  registeredCountryName?: string;
  quickbooksRealmId: Nullable<string>;
  quickbooksAccessToken: Nullable<string>;
  quickbooksRefreshToken: Nullable<string>;
  xeroRefreshToken: Nullable<string>;
  preferredTimezone?: Nullable<string>;
  businessHours?: Nullable<BusinessHours>;
  scheduledEventColourCoding?: Nullable<ScheduledEventColourCoding>;
  primaryPipelineId?: Nullable<Pipeline['id']>;
  subdomain: Nullable<string>;
  latitude?: Nullable<number>;
  longitude?: Nullable<number>;
  fieldServiceSupplierIds: number[];
  parentAccountId: Nullable<number>;
}

export interface AccountOutboundEmailDomain {
  domain: string;
  isDkimVerified: boolean;
  dkimPendingHost: string | null;
  dkimPendingTextValue: string | null;
  returnPathDomainCnameValue: string | null;
  emails: { email: string; isConfirmed: boolean }[];
}

export interface AccountOutboundEmails {
  domains: AccountOutboundEmailDomain[];
  defaults: {
    default: {
      email: string;
    } | null;
  };
}

/**
 * The account returned by the legacy REST profile endpoint
 * (`GET /api/users/me` → `accounts[0]`), as built by the backend's
 * `accountService.decorate` — change them together.
 *
 * Timestamps are ISO strings (JSON-serialised). Currency values are minor
 * units.
 */
export interface DecoratedAccount {
  // Columns passed through from the accounts table
  address: string | null;
  city: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  companyName: string | null;
  contactNumber: string | null;
  email: string | null;
  createdAt: string | null;
  id: number;
  logoUrl: string | null;
  updatedAt: string | null;
  vatNumber: string | null;
  proposalPaymentTerms: string | null;
  invoicePaymentTerms: string | null;
  uuid: string | null;
  legalBusinessName: string | null;
  isCisSubcontractor: boolean | null;
  cisDeductionRate: number | null;
  stripeCustomerId: string | null;
  businessNotes: string | null;
  brandColour: string | null;
  hideItemPrices: boolean;
  hideVatBreakdown: boolean | null;
  propositionValidForDays: number | null;
  invoiceDueInDays: number | null;
  dealViewActivatedAt: string | null;
  credits: number | null;
  gasSafeRegNo: string | null;
  niceicRegNo: string | null;
  mcsCertificationNumber: string | null;
  mcsBatteryStorageCertified: boolean;
  solarRetailTariffMinorPerKwh: number | null;
  solarExportTariffMinorPerKwh: number | null;
  solarPlacementRules: Record<string, unknown> | null;
  solarEessSettings: Record<string, unknown> | null;
  preferredTimezone: string | null;
  allowStripePaymentDefault: boolean | null;
  allowBankTransferPaymentDefault: boolean | null;
  leadCaptureToken: string | null;
  smsSenderHandle: string | null;
  numberOfEmployees: string | null;
  companyRegistrationNumber: string | null;
  registeredCountryName: string | null;
  defaultDepositPercentage: number | null;
  primaryPipelineId: number | null;
  externalPaymentRedirectUrl: string | null;

  // Derived, renamed, decrypted or narrowed fields
  /** @deprecated alias of proposalPaymentTerms */
  paymentTerms: string | null;
  accountNameRaw: string | null;
  accountNumberRaw: string | null;
  sortCodeRaw: string | null;
  activeSubscription: boolean;
  stripePaymentMethodBrand: string | null;
  stripePaymentMethodExpMonth: string | null;
  stripePaymentMethodExpYear: string | null;
  stripePaymentMethodLast4: string | null;
  stripeCurrentPeriodEndsAt: string | null;
  connectedToStripe: boolean;
  isTrialPeriodExpired: boolean;
  trialPeriodDaysRemaining: number | null;
  integrations: Integrations;
  outboundEmails: AccountOutboundEmails;
  /** industryTypeOther when set, otherwise the industryType column */
  industryType: string | null;
  region: AccountRegions | null;
  subdomain: string | null;
  businessHours: BusinessHours | null;
  scheduledEventColourCoding: ScheduledEventColourCoding | null;
}

export interface BusinessAttachment {
  attachmentUrl: string;
  createdAt: string;
  fileName: string;
  id: string;
  updatedAt: string;
  account_id: string;
}

export interface AccountAccessInformation {
  hasActiveSubscription: boolean;
  hasExceededActiveSubscriptionUserSeats: boolean;
  hasInactiveSubscription: boolean;
  isTrialPeriodExpired: boolean;
  trialPeriodDaysRemaining: number | null;
  onboardingRequestedFor: Date | null;
  features: Features;
  temporarySubscription: null | {
    name: string;
    expiresAt: string;
  };
  discountPercentage?: Nullish<number>;
  discountAvailableUntil?: Nullish<number>;
}

// decorated account interface for customer preview
export interface AccountForPreview {
  id: number;
  address?: string;
  postcode?: string;
  companyName: string;
  city?: string;
  contactNumber?: string;
  email?: string;
  fallbackEmail: string;
  logoUrl?: string;
  brandColour?: string;
  businessNotes?: string;
  uuid: string;
  legalBusinessName: string;
  region: AccountRegions;

  // @deprecated
  paymentTerms?: string;
  proposalPaymentTerms?: Nullish<string>;
  invoicePaymentTerms?: Nullish<string>;
  accountNumberRaw?: string;
  accountNameRaw?: string;

  sortCodeRaw?: string;
  vatNumber?: string;

  integrations: object;
}

export interface AccountAccessInformationForPreview {
  features: object;
}

export interface EmailTemplates {
  sendQuote: string;
  sendInvoice: string;
  sendEstimate: string;
}
export interface AccountEmailTemplates extends EmailTemplates {
  defaults?: EmailTemplates;
}

export interface CreateAccountRequestData {
  legalBusinessName: string;
  email: string;
  contactNumber: string;
  numberOfEmployees: string;
  industryType: string;
  industryTypeOther?: string;
}
